import { useId } from 'react';
import ActionLink from '../partials/ActionLink';
import googleMapsLogo from '../../img/google-maps-attribution.png';
import '../../css/GoogleReviews.css';

function safeGoogleUrl(value) {
    if (typeof value !== 'string' || value.length > 2048) return null;
    try {
        const url = new URL(value);
        if (url.protocol !== 'https:' || url.username || url.password || url.port) return null;
        const { hostname, pathname } = url;
        if (['maps.google.com', 'maps.google.it'].includes(hostname)) return url.href;
        if (['google.com', 'www.google.com', 'google.it', 'www.google.it'].includes(hostname)
            && (/^\/maps(?:\/|$)/.test(pathname) || pathname === '/local/writereview' || pathname === '/search')) return url.href;
        if (['maps.app.goo.gl', 'g.page'].includes(hostname) && pathname.length > 1) return url.href;
        if (hostname === 'goo.gl' && /^\/maps\/.+/.test(pathname)) return url.href;
    } catch { /* Ignore malformed external links. */ }
    return null;
}

const ratingFormat = new Intl.NumberFormat('it-IT', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const countFormat = new Intl.NumberFormat('it-IT');

// Ratings come from the API response and are kept in memory, never in browser storage.
export default function GoogleReviews({ reviews, agencyId, source, compact = false, ratingOnly = false }) {
    const headingId = useId();
    const url = safeGoogleUrl(reviews?.url);
    if (!url) return null;

    const count = Number.isSafeInteger(reviews.reviewCount) && reviews.reviewCount >= 0 ? reviews.reviewCount : null;
    const rating = typeof reviews.rating === 'number' && Number.isFinite(reviews.rating)
        && reviews.rating >= 1 && reviews.rating <= 5 && count !== 0 ? reviews.rating : null;
    const ratingText = rating === null ? null : ratingFormat.format(rating);
    const countText = count === null ? null : count === 0 ? 'Nessuna recensione'
        : `${countFormat.format(count)} ${count === 1 ? 'recensione' : 'recensioni'}`;
    const unavailableText = count === 0 ? 'Non ancora valutata' : 'Valutazione non disponibile';
    const unavailable = <span className="ef-google-reviews__unavailable">{unavailableText}</span>;
    const stars = rating !== null && <span className="ef-google-reviews__stars" aria-hidden="true"><span>★★★★★</span><span style={{ width: `${rating * 20}%` }}>★★★★★</span></span>;
    const ratingSummary = rating !== null && <span className="ef-google-reviews__rating" role="img" aria-label={`Valutazione ${ratingText} su 5`}>
        <strong aria-hidden="true">{ratingText}</strong>{stars}
    </span>;
    const attribution = <span className="ef-google-reviews__attribution" translate="no">Google Maps</span>;

    if (ratingOnly) return <div className={`ef-google-reviews ef-google-reviews--rating-only${rating === null ? ' ef-google-reviews--unavailable' : ''}`} aria-label="Valutazione Google">
        {ratingSummary || unavailable}{attribution}
    </div>;

    if (compact) return <div className="ef-google-reviews ef-google-reviews--compact" aria-label="Recensioni Google">
        <div className="ef-google-reviews__summary">{ratingSummary || unavailable}{countText && <span className="ef-google-reviews__count">{countText}</span>}{attribution}</div>
        <ActionLink action="reviews" agencyId={agencyId} source={source} className="ef-google-reviews__link" href={url} target="_blank" rel="noopener noreferrer" aria-label="Recensioni su Google (si apre in una nuova scheda)">Recensioni su Google <span aria-hidden="true">↗</span></ActionLink>
    </div>;

    const linkLabel = [ratingText ? `Valutazione ${ratingText} su 5.` : `${unavailableText}.`, `${countText || 'Totale recensioni non disponibile'}.`, 'Vedi le recensioni su Google (si apre in una nuova scheda)'].join(' ');

    return <section className="ef-agency-section ef-google-reviews" aria-labelledby={headingId}>
        <h2 id={headingId}>Recensioni su Google</h2>
        <ActionLink action="reviews" agencyId={agencyId} source={source} className="ef-google-reviews__panel" href={url} target="_blank" rel="noopener noreferrer" aria-label={linkLabel}>
            <div className="ef-google-reviews__header">
                <img className="ef-google-reviews__logo" src={googleMapsLogo} alt="Google Maps" width="98" height="18" />
                <span className="ef-google-reviews__external" aria-hidden="true">↗</span>
            </div>
            {rating !== null ? <div className="ef-google-reviews__score">
                <span className="ef-google-reviews__rating" role="img" aria-label={`Valutazione ${ratingText} su 5`}>
                    <strong aria-hidden="true">{ratingText}</strong><span className="ef-google-reviews__scale" aria-hidden="true">/ 5</span>
                </span>
                <div className="ef-google-reviews__breakdown">
                    {stars}
                    <span className="ef-google-reviews__count">{countText || 'Totale recensioni non disponibile'}</span>
                </div>
            </div> : <div className="ef-google-reviews__empty">
                {unavailable}
                <span className="ef-google-reviews__count">{countText || 'Totale recensioni non disponibile'}</span>
                <p className="ef-google-reviews__note">{count === 0 ? 'Questa attività non ha ancora recensioni su Google.' : 'Consulta le recensioni dell’attività su Google.'}</p>
            </div>}
            <div className="ef-google-reviews__footer">
                <span className="ef-google-reviews__caption">Le esperienze su Google</span>
                <span className="ef-google-reviews__cta">Leggi le recensioni <span aria-hidden="true">↗</span></span>
            </div>
        </ActionLink>
    </section>;
}
